import React, { useCallback, useEffect, useState } from "react";
import axios from "../../api";
import {
  Container,
  Paper,
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Pagination,
} from "@mui/material";
import { FactCheck, CheckCircle, Cancel } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import AdminLayout from "../AdminLayout";
import UndertakingDocuments from "../common/UndertakingDocuments";

// Human labels for the undertaking field names stored in discrepancies.
const FIELD_LABELS = {
  assetId: "Asset Tag",
  serialNumber: "Serial Number",
  Model: "Model",
  ModelNo: "Model No",
  Manufacturer: "Manufacturer",
  Specifications: "Specifications",
};

const PER_PAGE = 10;

function DiscrepancyReview({ onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [busyId, setBusyId] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [docsRecord, setDocsRecord] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get("/undertaking/discrepancies");
      setRecords(res.data.result || []);
      setPage(1);
    } catch (err) {
      toast.error("Failed to load discrepancies.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resolve = async (id, action, reason) => {
    setBusyId(id);
    try {
      await axios.post(`/undertaking/${id}/discrepancy/resolve`, { action, reason });
      toast.success(
        action === "accept"
          ? "Corrections applied to inventory."
          : "Correction rejected; inventory unchanged."
      );
      setRejectTarget(null);
      setRejectReason("");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Action failed.");
    } finally {
      setBusyId(null);
    }
  };

  const handleLogout = () => {
    if (onLogout) return onLogout();
    localStorage.clear();
    navigate("/login");
  };

  const pageCount = Math.ceil(records.length / PER_PAGE) || 1;
  const safePage = Math.min(page, pageCount);
  const paged = records.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

  return (
    <AdminLayout
      title="Corrections"
      subtitle="Employee-submitted device detail corrections — accept to update inventory, or reject to keep records as-is. (Separate from Dashboard invite approvals.)"
      onLogout={handleLogout}
    >
      <Container maxWidth="lg">
        <Paper elevation={3} sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <FactCheck color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Pending corrections
            </Typography>
            <Chip size="small" label={records.length} color="warning" />
          </Box>
          <Divider sx={{ mb: 2 }} />

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : records.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <CheckCircle color="success" sx={{ fontSize: 56, mb: 1 }} />
              <Typography variant="h6">All clear</Typography>
              <Typography variant="body2" color="text.secondary">
                No pending device-detail corrections to review.
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small" sx={{ minWidth: 800 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Employee</TableCell>
                      <TableCell>Device</TableCell>
                      <TableCell>Submitted corrections</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paged.map((r) => (
                      <TableRow key={r._id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {r.EmpName || "-"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {r.email}
                            {r.EmpID ? ` · ${r.EmpID}` : ""}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {r.asset?.assetTag || r.assetId || "-"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {r.asset?.serialNumber || r.serialNumber || ""}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {(r.discrepancies || []).map((d, i) => (
                            <Box key={i} sx={{ mb: 0.75 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block" }}
                              >
                                {FIELD_LABELS[d.field] || d.field}
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  flexWrap: "wrap",
                                }}
                              >
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  color="default"
                                  label={`Records: ${d.expected || "—"}`}
                                />
                                <Chip
                                  size="small"
                                  color="warning"
                                  label={`Employee: ${d.provided || "—"}`}
                                />
                              </Box>
                            </Box>
                          ))}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {r.timestamp
                              ? new Date(r.timestamp).toLocaleDateString()
                              : "-"}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              justifyContent: "flex-end",
                              flexWrap: "nowrap",
                            }}
                          >
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setDocsRecord(r)}
                            >
                              Photos
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<CheckCircle />}
                              disabled={busyId === r._id}
                              onClick={() => resolve(r._id, "accept")}
                            >
                              Accept
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<Cancel />}
                              disabled={busyId === r._id}
                              onClick={() => setRejectTarget(r)}
                            >
                              Reject
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <Pagination
                  count={pageCount}
                  page={safePage}
                  onChange={(e, v) => setPage(v)}
                />
              </Box>
            </>
          )}
        </Paper>
      </Container>

      <Dialog
        open={Boolean(docsRecord)}
        onClose={() => setDocsRecord(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Uploads — {docsRecord?.EmpName || docsRecord?.email}
        </DialogTitle>
        <DialogContent dividers>
          {docsRecord && (
            <UndertakingDocuments
              idProofPath={docsRecord.idProofPath}
              signaturePath={docsRecord.signaturePath}
              assetImages={docsRecord.assetImages}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDocsRecord(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(rejectTarget)}
        onClose={() => setRejectTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject correction</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Inventory will stay unchanged. Optionally note why the correction was
            rejected.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Reason (optional)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectTarget(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => resolve(rejectTarget._id, "reject", rejectReason)}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      <ToastContainer position="top-center" autoClose={2500} />
    </AdminLayout>
  );
}

export default DiscrepancyReview;
